import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

/** Ultima resposta boa de uma URL: o ETag que o backend devolveu e o corpo. */
interface RespostaCondicional {
  etag: string;
  body: unknown;
}

/**
 * Cache condicional (ETag / If-None-Match) para os GETs da API.
 *
 * O refresh automatico manda If-None-Match com o ETag da resposta anterior;
 * quando o backend responde 304 ("nada mudou") reaproveitamos o corpo ja
 * recebido - nenhum JSON trafega e o componente nem e notificado com dados
 * novos (o mesmaLista() do polling continua sendo a ultima barreira).
 *
 * O navegador faz isso sozinho por causa do "Cache-Control: no-cache" que o
 * backend envia; aqui e explicito, funciona igual com proxy/CDN no meio e
 * resolve o detalhe de que o HttpClient trata 304 como erro (nao e 2xx).
 */
@Injectable()
export class EtagCacheInterceptor implements HttpInterceptor {

  private readonly cache = new Map<string, RespostaCondicional>();

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Só leitura: POST/PUT/DELETE nao tem cache condicional.
    if (request.method !== 'GET') {
      return next.handle(request);
    }

    const chave = request.urlWithParams;
    const anterior = this.cache.get(chave);
    const requisicao = anterior
      ? request.clone({ setHeaders: { 'If-None-Match': anterior.etag } })
      : request;

    return next.handle(requisicao).pipe(
      tap((evento) => {
        if (evento instanceof HttpResponse) {
          const etag = evento.headers.get('ETag');
          if (etag) {
            this.cache.set(chave, { etag, body: evento.body });
          }
        }
      }),
      catchError((erro: unknown) => {
        const semAlteracao = erro instanceof HttpErrorResponse && erro.status === 304;

        if (semAlteracao && anterior) {
          return of(
            new HttpResponse({
              body: anterior.body,
              status: 200,
              statusText: 'OK (304 - sem alteracoes)',
              url: request.urlWithParams
            })
          );
        }

        return throwError(() => erro);
      })
    );
  }
}
